import { Text } from '@acast/decibel';
import classnames from 'classnames';
import T from 'prop-types';
import React, { Component } from 'react';
import uploader from 'services/file-upload';
import { presentModal } from 'services/modals';
import Modal from 'shared/components/modal';
import ReplaceableImage from 'shared/components/replaceable-image';
import CropDialog from './crop-dialog';

function isNumBetween(value, min, max) {
  return value >= min && value <= max;
}

export default class UploadableImage extends Component {
  static propTypes = {
    entity: T.string.isRequired,
    name: T.string.isRequired,
    enabled: T.bool,
    src: T.string.isRequired,
    onImageUploaded: T.func.isRequired,
    onImageRemoved: T.func.isRequired,
    enableRemove: T.bool,
    minimumImageSize: T.number,
    maximumImageSize: T.number,
  };

  static defaultProps = {
    name: '',
    enabled: true,
    src: '',
    enableRemove: true,
    minimumImageSize: 1400,
    maximumImageSize: 3000,
  };

  state = {
    previewURL: null,
    error: null,
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { props } = this;

    if (nextProps.entity !== props.entity) {
      this.clearPreview();
    }
  }

  getUploadProgress() {
    const { entity, name } = this.props;
    const existing = uploader.get(entity, name);
    const inProgress = existing !== null;

    if (inProgress) {
      const percent = Math.floor(existing.progress * 100);
      return (
        <div className="upload-progress">
          <div className="percent">UPLOADING {percent}%</div>
          <div className="progress-bar">
            <div className="progress-bar--inner" style={{ width: `${percent}%` }} />
          </div>
        </div>
      );
    }
    return null;
  }

  handleImageReplaced = file => {
    this.createFilePreview(file);
  };

  clearPreview() {
    if (this.state.previewURL) {
      URL.revokeObjectURL(this.state.previewURL);
      this.setState({ previewURL: null });
    }
  }

  handleImageRemoved = () => {
    this.clearPreview();
    this.props.onImageRemoved();
  };

  presentCropModal(objectURL) {
    return presentModal(<CropDialog url={objectURL} />);
  }

  makeImageSquare(file, width, height) {
    if (width === height) {
      return Promise.resolve(file);
    }

    const objectURL = URL.createObjectURL(file);
    return this.presentCropModal(objectURL).then(croppedBlob => {
      URL.revokeObjectURL(objectURL);
      const croppedFile = croppedBlob;
      croppedFile.name = file.name;
      croppedFile.lastModifiedDate = file.lastModifiedDate;
      return croppedFile;
    });
  }

  beginFileUpload(file) {
    const { entity, name } = this.props;
    uploader.begin({ path: entity, id: entity }, name, file).then(url => {
      this.props.onImageUploaded(url);
    });
  }

  showInvalidSizeError(width, height) {
    const { minimumImageSize, maximumImageSize } = this.props;
    presentModal(
      <Modal
        title="Invalid size"
        dialog={
          <Text mb={0}>
            The image your are attempting to upload does not meet the required dimensions. It needs
            to be at least {minimumImageSize}x{minimumImageSize} pixels and no more than{' '}
            {maximumImageSize}x{maximumImageSize}. Your image is {width}x{height}.
            <br />
            <br />
            Please ensure your image falls within those dimensions before uploading.
          </Text>
        }
        actions={Modal.Actions.OK}
        onDismiss={() => {}}
      />
    );
  }

  showUnsupportedError() {
    presentModal(
      <Modal
        title="Unsupported browser"
        dialog="Your browser does not seem to support the necessary features to validate and upload an image. Please use a more modern browser"
        actions={Modal.Actions.OK}
      />
    );
  }

  isDimensionValid(value) {
    const { minimumImageSize, maximumImageSize } = this.props;
    return isNumBetween(value, minimumImageSize, maximumImageSize);
  }

  createFilePreview(file) {
    if ('URL' in window) {
      const objectURL = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        URL.revokeObjectURL(objectURL);
        const { width, height } = image;

        if (this.isDimensionValid(width) && this.isDimensionValid(height)) {
          this.makeImageSquare(file, width, height).then(squareImageFile => {
            this.setState({ previewURL: URL.createObjectURL(squareImageFile) });
            this.beginFileUpload(squareImageFile);
          });
        } else {
          this.showInvalidSizeError(width, height);
          this.clearPreview();
        }
      };

      image.src = objectURL;
    } else {
      this.showUnsupportedError();
    }
  }

  render() {
    const { previewURL } = this.state;
    const { src, enabled, entity, name, enableRemove } = this.props;
    const existing = uploader.get(entity, name);
    const inProgress = existing !== null;

    return (
      <div className={classnames('uploadable-image', { 'upload-in-progress': inProgress })}>
        <ReplaceableImage
          enabled={!inProgress && enabled}
          src={previewURL || src}
          onImageReplaced={this.handleImageReplaced}
          onImageRemoved={this.handleImageRemoved}
          enableRemove={enableRemove}
        />
        {this.getUploadProgress()}
      </div>
    );
  }
}
